import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ReactNode } from 'react';
import './Table.css';

/**
 * Table component props
 */
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /**
   * Whether the table is in a loading state
   */
  isLoading?: boolean;
  /**
   * Number of columns for skeleton rows
   */
  skeletonColumns?: number;
  /**
   * Number of skeleton rows to show
   */
  skeletonRows?: number;
}

/**
 * Table component for displaying data in rows and columns
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableHead>
 *     <TableRow>
 *       <TableHeader>Name</TableHeader>
 *       <TableHeader>Status</TableHeader>
 *     </TableRow>
 *   </TableHead>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>Node 1</TableCell>
 *       <TableCell>Online</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * ```
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      children,
      isLoading = false,
      skeletonColumns = 4,
      skeletonRows = 5,
      className = '',
      ...props
    },
    ref
  ) => {
    const classNames = ['table', className].filter(Boolean).join(' ');

    if (isLoading) {
      return (
        <div className="table-wrapper">
          <table ref={ref} className={classNames} {...props}>
            <TableHead>
              <TableRow>
                {Array.from({ length: skeletonColumns }).map((_, i) => (
                  <TableHeader key={i}>
                    <div className="table-skeleton table-skeleton-header" />
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: skeletonColumns }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <div className="table-skeleton table-skeleton-cell" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
      );
    }

    return (
      <div className="table-wrapper">
        <table ref={ref} className={classNames} {...props}>
          {children}
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';

/**
 * TableHead component props
 */
export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

/**
 * Table head component for header rows
 */
export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <thead ref={ref} className={`table-head ${className}`} {...props}>
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

/**
 * TableBody component props
 */
export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

/**
 * Table body component for data rows
 */
export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ children, className = '', ...props }, ref) => {
    return (
      <tbody ref={ref} className={`table-body ${className}`} {...props}>
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

/**
 * TableRow component props
 */
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
  /**
   * Whether this row is clickable
   */
  clickable?: boolean;
  /**
   * Whether this row is selected
   */
  selected?: boolean;
}

/**
 * Table row component
 */
export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, clickable = false, selected = false, className = '', ...props }, ref) => {
    const classNames = [
      'table-row',
      clickable ? 'table-row-clickable' : '',
      selected ? 'table-row-selected' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <tr ref={ref} className={classNames} {...props}>
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

/**
 * TableHeader component props
 */
export interface TableHeaderProps
  extends HTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  /**
   * Alignment of the header content
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Whether this column is sortable
   */
  sortable?: boolean;
  /**
   * Current sort direction
   */
  sortDirection?: 'asc' | 'desc' | null;
  /**
   * Callback when sort is clicked
   */
  onSort?: () => void;
}

/**
 * Table header cell component
 */
export const TableHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(
  (
    {
      children,
      align = 'left',
      sortable = false,
      sortDirection,
      onSort,
      className = '',
      ...props
    },
    ref
  ) => {
    const classNames = [
      'table-header',
      `table-align-${align}`,
      sortable ? 'table-header-sortable' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClick = () => {
      if (sortable && onSort) {
        onSort();
      }
    };

    return (
      <th
        ref={ref}
        className={classNames}
        onClick={sortable ? handleClick : undefined}
        role={sortable ? 'button' : undefined}
        tabIndex={sortable ? 0 : undefined}
        aria-sort={
          sortDirection === 'asc'
            ? 'ascending'
            : sortDirection === 'desc'
            ? 'descending'
            : undefined
        }
        {...props}
      >
        <span className="table-header-content">
          {children}
          {sortable && (
            <span className="table-sort-icon" aria-hidden="true">
              {sortDirection === 'asc' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5l7 7H5l7-7z" />
                </svg>
              )}
              {sortDirection === 'desc' && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 19l-7-7h14l-7 7z" />
                </svg>
              )}
              {!sortDirection && (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5l4 4H8l4-4zM12 19l-4-4h8l-4 4z" />
                </svg>
              )}
            </span>
          )}
        </span>
      </th>
    );
  }
);

TableHeader.displayName = 'TableHeader';

/**
 * TableCell component props
 */
export interface TableCellProps
  extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  /**
   * Alignment of the cell content
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
}

/**
 * Table data cell component
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, align = 'left', className = '', ...props }, ref) => {
    const classNames = ['table-cell', `table-align-${align}`, className]
      .filter(Boolean)
      .join(' ');

    return (
      <td ref={ref} className={classNames} {...props}>
        {children}
      </td>
    );
  }
);

TableCell.displayName = 'TableCell';

/**
 * Empty state component for tables
 */
export interface TableEmptyProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Message to display
   */
  message?: string;
  /**
   * Icon to display
   */
  icon?: ReactNode;
  /**
   * Action button or link
   */
  action?: ReactNode;
}

/**
 * Table empty state component
 */
export function TableEmpty({
  message = 'No data available',
  icon,
  action,
  className = '',
  ...props
}: TableEmptyProps) {
  return (
    <div className={`table-empty ${className}`} {...props}>
      {icon && <div className="table-empty-icon">{icon}</div>}
      <p className="table-empty-message">{message}</p>
      {action && <div className="table-empty-action">{action}</div>}
    </div>
  );
}
